import React, { useEffect, useState } from "react";
import { Button, Col, FloatingLabel, Spinner, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import Logo from "./img/new-project.webp";
import { useDomainNewMutation } from "./../../../graphql/projects.hooks";
import { UserDocument, useUserQuery } from "../../../graphql/menu.hooks";
import { projectLimitModal } from "../../../graphql/state";
import { FREE_LIMIT } from "../../../utils";
import StepsInfo from "../StepsInfo";

export default function AddNewProject() {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");

  const [addNewDomain, { loading }] = useDomainNewMutation();
  const { data: userData } = useUserQuery();
  const isPremium = userData?.user.activePremium;

  useEffect(() => {
    document.title = "New Project";
  }, []);

  const handleAddProject = async () => {
    if (
      userData?.user.domains.length >= FREE_LIMIT.PROJECT &&
      !userData?.user.admin &&
      !isPremium
    ) {
      projectLimitModal(true);
      return;
    }
    addNewDomain({
      variables: {
        title,
      },
      onCompleted: (data) => {
        navigate(`${lng}/projects/new/${data.domainNew.id}/setup`);
      },
      refetchQueries: [{ query: UserDocument }],
    });
  };

  return (
    <div className="px-lg-3">
      <h1 className="fs-1 fw-bold">{t("projects.Add New Project")}</h1>
      <div className="text-gray mb-4 mb-md-5">
        <Link to={`${lng}/projects`}>{t("payment.Projects")}</Link>
        <span className="px-2">/</span>
        {t("projects.Add New Project")}
      </div>
      <div className="text-dark bg-white p-4 p-md-5 rounded">
        <StepsInfo
          step={1}
          stepNames={[
            t("projects.Project Name"),
            t("projects.Setup Domain"),
            t("projects.Complite Setup"),
          ]}
          className="pb-md-4 mx-auto col-lg-10 col-xl-8 col-xxl-7"
        />
        <Col
          md={8}
          lg={4}
          className="firstStep mx-auto text-center mt-4 mt-md-5"
        >
          <img
            src={Logo}
            loading="lazy"
            alt="New Domain"
            className="img-fluid mb-4 pb-md-2"
          />
          <h3 className="mb-md-3 text-capitalize">
            {t("projects.Add New Project")}
          </h3>
          <p className="text-gray mx-auto mb-4 fs-7 pb-md-2">
            {t("projects.Create a new project to organize your pages")}
          </p>
          <FloatingLabel
            label={t("projects.Project Title")}
            className="mb-4 text-gray"
          >
            <Form.Control
              type="text"
              maxLength={255}
              placeholder={t("projects.Project Title")}
              onChange={(e) => setTitle(e.target.value.trim())}
            />
          </FloatingLabel>
          <div className="pt-md-2 gy-3 d-flex justify-content-center">
            <Col className="me-3 me-md-4">
              <Link to={`${lng}/projects`}>
                <Button
                  variant="outline-dark"
                  className="w-100 fw-normal dark-hover"
                >
                  {t("projects.Cancel")}
                </Button>
              </Link>
            </Col>
            <Col>
              <Button
                variant="primary"
                className="w-100 text-white fw-bold"
                onClick={handleAddProject}
                disabled={loading || !title.trim().length}
              >
                <>
                  {t("projects.Add")}
                  {loading && (
                    <Spinner
                      animation="border"
                      role="status"
                      size="sm"
                      className="ms-2"
                    />
                  )}
                </>
              </Button>
            </Col>
          </div>
        </Col>
      </div>
    </div>
  );
}
